import type { SupabaseClient } from '@supabase/supabase-js'
import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgProofsForBrandSettings } from 'src/api/db/apiProof'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { queryKeys } from 'src/api/db/queryKeys'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { APPROVED_PROOFS_STATUS } from 'src/shared/consts/quoteStatus'
import { useToast } from 'src/shared/hooks/use-toast'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOptionalRequestContext } from '../../../hooks/RequestContext'
import {
  getFilenameFromUrl,
  proofEntriesFromQuoteProofLinks,
  type ProofStatus,
} from 'src/shared/utils/proofUrls'
import type { Database } from 'supabase/database.types'
import { PROOFS_BUCKET } from './consts'
import { DeleteProofDialog } from './components/DeleteProofDialog'
import { ProofList } from './components/ProofList'
import { ProofUploadButton } from './components/ProofUploadButton'
import { VerifyProofsEmptyState } from './components/VerifyProofsEmptyState'
import type { OrgReuseProofRow, VerifyProofsProps } from './types'
import { sanitizeFilename } from './utils'

async function getNextQuoteProofSortOrder(
  supabase: SupabaseClient<Database>,
  quoteId: number
): Promise<number> {
  const { data, error } = await supabase
    .from('quote_proof')
    .select('sort_order')
    .eq('quote_id', quoteId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return (data?.sort_order ?? -1) + 1
}

export const VerifyProofs = ({
  quote,
  isShareOrder,
  setCurrentStep,
  embedded = false,
}: VerifyProofsProps) => {
  const { t } = useLocalization()
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const { isDemo, onDemoAdvance } = useOptionalRequestContext() ?? {}
  const orgId = quote?.org_id ?? undefined
  const { rows: orgProofBrandRows } = useOrgProofsForBrandSettings({
    org_id: orgId,
  })
  const orderNotifications = useOrderNotifications()
  const isAdmin = useIsAdmin()

  const refreshQuotes = async () => {
    await queryClient.refetchQueries({
      queryKey: [queryKeys.quotes],
      type: 'all',
    })
  }

  const toastUpdateFailed = (description?: string) =>
    toast({
      variant: 'destructive',
      title: t('Update failed', 'Uppdatering misslyckades'),
      ...(description ? { description } : {}),
    })

  const [isUploading, setIsUploading] = useState(false)
  const [proofToRemove, setProofToRemove] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const proofs = proofEntriesFromQuoteProofLinks(quote?.quote_proof)
  const quoteId = quote?.id
  const quotePublicId = quote?.public_id
  const hasProofs = proofs.length > 0

  const proofUrlsOnThisOrder = useMemo(
    () => new Set(proofs.map((p) => p.url)),
    [proofs]
  )

  const reuseProofRows = useMemo((): OrgReuseProofRow[] => {
    if (!orgId) return []
    return orgProofBrandRows.map((row) => ({
      url: row.proof.url,
      filename: getFilenameFromUrl(row.proof.url),
      orderLabel: row.proof.created_at
        ? new Date(row.proof.created_at).toLocaleDateString()
        : '',
      alreadyOnThisOrder: proofUrlsOnThisOrder.has(row.proof.url),
    }))
  }, [orgId, orgProofBrandRows, proofUrlsOnThisOrder])

  const attachExistingProofs = async (urls: string[]): Promise<boolean> => {
    if (!quoteId || !orgId || urls.length === 0) return false

    try {
      const existingUrls = new Set(proofs.map((p) => p.url))
      const toAdd = urls.filter((u) => !existingUrls.has(u))
      if (toAdd.length === 0) {
        await refreshQuotes()
        return true
      }

      let sortOrder = await getNextQuoteProofSortOrder(supabase, quoteId)

      for (const url of toAdd) {
        const proofResult = await supabase
          .from('proof')
          .upsert({ org_id: orgId, url }, { onConflict: 'org_id,url' })
          .select('id')
          .single()

        if (proofResult.error) throw proofResult.error
        const proof_id = proofResult.data.id

        const { data: already } = await supabase
          .from('quote_proof')
          .select('proof_id')
          .eq('quote_id', quoteId)
          .eq('proof_id', proof_id)
          .maybeSingle()

        if (already) continue

        const insertResult = await supabase.from('quote_proof').insert({
          quote_id: quoteId,
          proof_id,
          sort_order: sortOrder,
          status: 'pending',
        })

        if (insertResult.error) throw insertResult.error
        sortOrder += 1
      }

      await refreshQuotes()
      return true
    } catch (err) {
      console.error('attachExistingProofs error:', err)
      toastUpdateFailed(err instanceof Error ? err.message : undefined)
      return false
    }
  }

  const patchProofComment = async (proofId: string, comment: string) => {
    if (isDemo || !quoteId) return

    const customer_comment = comment.trim() === '' ? null : comment

    const { error } = await supabase
      .from('quote_proof')
      .update({ customer_comment })
      .eq('quote_id', quoteId)
      .eq('proof_id', proofId)

    if (error) {
      console.error('patchProofComment error:', error)
      toastUpdateFailed(error.message)
      return
    }

    await refreshQuotes()
  }

  const patchProofStatus = async (url: string, status: ProofStatus) => {
    if (isDemo) {
      if (status === 'approved') onDemoAdvance?.('approve_proofs')
      return
    }
    if (!quoteId || !orgId) return

    const { data: proofRow, error: proofErr } = await supabase
      .from('proof')
      .select('id')
      .eq('org_id', orgId)
      .eq('url', url)
      .maybeSingle()

    if (proofErr) {
      console.error('patchProofStatus proof lookup error:', proofErr)
      toastUpdateFailed(proofErr.message)
      return
    }

    if (!proofRow) {
      toastUpdateFailed()
      return
    }

    const qpUpdate = await supabase
      .from('quote_proof')
      .update({ status })
      .eq('quote_id', quoteId)
      .eq('proof_id', proofRow.id)

    if (qpUpdate.error) {
      console.error(
        'patchProofStatus quote_proof update error:',
        qpUpdate.error
      )
      toastUpdateFailed(qpUpdate.error.message)
      return
    }

    if (status === 'approved') {
      const allApproved = proofs.every((proof) =>
        proof.url === url ? true : proof.status === 'approved'
      )
      if (!allApproved) {
        await refreshQuotes()
        return
      }

      try {
        await updateQuote.mutateAsync({
          id: quoteId,
          status: APPROVED_PROOFS_STATUS,
        })
        if (quotePublicId) {
          await orderNotifications.mutateAsync({
            kind: 'lifecycle',
            actorRole: 'customer',
            subject:
              t(
                '✅ 📷 Proofs approved for order ',
                '✅ 📷 Korrekturer godkända för order '
              ) + `#${quotePublicId}`,
            orderNotification: {
              quotePublicId,
              headerBadge: t('Proofs approved', 'Korrekturer godkända'),
              headerTitle: t(
                'Proofs approved for order',
                'Korrekturer godkända för order'
              ),
              message: t(
                'The customer has approved the proofs. Click below to view the order and proceed to checkout.',
                'Kunden har godkänt korrekturen. Klicka nedan för att visa ordern och fortsätta till kassan.'
              ),
              buttonText: t('View order', 'Se order'),
            },
          })
        }
        setCurrentStep?.('checkout_tab')
      } catch (err) {
        console.error('Approve proofs error:', err)
        toastUpdateFailed(err instanceof Error ? err.message : undefined)
      }
    } else {
      if (status === 'not_approved' && quotePublicId) {
        try {
          await orderNotifications.mutateAsync({
            kind: 'lifecycle',
            actorRole: 'customer',
            subject:
              t(
                'Proof changes requested for order ',
                'Ändringar begärda på korrektur för order '
              ) + `#${quotePublicId}`,
            orderNotification: {
              quotePublicId,
              headerBadge: t('Proof changes requested', 'Ändringar begärda'),
              headerTitle: t(
                'Customer requested proof changes',
                'Kunden begärde ändringar på korrektur'
              ),
              message: t(
                'The customer did not approve a proof. Click below to view the order and follow up.',
                'Kunden godkände inte en korrektur. Klicka nedan för att visa ordern och följa upp.'
              ),
              buttonText: t('View order', 'Se order'),
            },
          })
        } catch (err) {
          console.error('Proof reject notification error:', err)
        }
      }
      await refreshQuotes()
    }
  }

  const handleUpload = async (files: File[]) => {
    if (!quoteId || !orgId || files.length === 0) return

    try {
      setIsUploading(true)
      const base = Date.now()
      let newProofsLinked = 0

      for (let i = 0; i < files.length; i++) {
        const file = files[i]!
        const sanitizedName = sanitizeFilename(file.name)
        const fileName = `${base}-${i}-${sanitizedName}`
        const filePath = `${quoteId}/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from(PROOFS_BUCKET)
          .upload(filePath, file)

        if (uploadError) throw uploadError

        const {
          data: { publicUrl },
        } = supabase.storage.from(PROOFS_BUCKET).getPublicUrl(filePath)

        const proofResult = await supabase
          .from('proof')
          .upsert(
            { org_id: orgId, url: publicUrl },
            { onConflict: 'org_id,url' }
          )
          .select('id')
          .single()

        if (proofResult.error) throw proofResult.error

        const { data: existingLink } = await supabase
          .from('quote_proof')
          .select('proof_id')
          .eq('quote_id', quoteId)
          .eq('proof_id', proofResult.data.id)
          .maybeSingle()

        if (!existingLink) {
          const sortOrder = await getNextQuoteProofSortOrder(supabase, quoteId)

          const qpResult = await supabase.from('quote_proof').insert({
            quote_id: quoteId,
            proof_id: proofResult.data.id,
            sort_order: sortOrder,
            status: 'pending',
          })

          if (qpResult.error) throw qpResult.error
          newProofsLinked += 1
        }
      }

      if (newProofsLinked > 0 && quotePublicId) {
        const plural = newProofsLinked > 1
        await orderNotifications.mutateAsync({
          kind: 'lifecycle',
          actorRole: 'admin',
          subject:
            (plural
              ? t('📷 New proofs added for ', '📷 Nya korrektur tillagda för ')
              : t('📷 New proof added for ', '📷 Nytt korrektur tillagt för ')) +
            `order #${quotePublicId}`,
          orderNotification: {
            quotePublicId: quotePublicId,
            headerBadge: plural
              ? t('New proofs', 'Nya korrekturer')
              : t('New proof', 'Nytt korrektur'),
            headerTitle: plural
              ? t(
                  'New proofs added for order',
                  'Nya korrekturer tillagda för order'
                )
              : t(
                  'New proof added for order',
                  'Nytt korrektur tillagt för order'
                ),
            message: plural
              ? t(
                  'New proofs have been uploaded for the order. Click below to view and review.',
                  'Nya korrekturer har laddats upp för ordern. Klicka nedan för att visa och granska.'
                )
              : t(
                  'A new proof has been uploaded for the order. Click below to view and review.',
                  'Ett nytt korrektur har laddats upp för ordern. Klicka nedan för att visa och granska.'
                ),
            buttonText: t('View order', 'Se order'),
          },
        })
      }

      await refreshQuotes()
      if (orgId) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.proofs, 'brandSettings', orgId],
        })
      }
    } catch (err) {
      console.error('Proof upload error:', err)
      toast({
        variant: 'destructive',
        title: t('Upload failed', 'Uppladdning misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async (urlToRemove: string) => {
    if (!quoteId || !orgId) return

    try {
      const { data: proofRow, error: proofErr } = await supabase
        .from('proof')
        .select('id')
        .eq('org_id', orgId)
        .eq('url', urlToRemove)
        .maybeSingle()

      if (proofErr) throw proofErr
      if (!proofRow) return

      const { error: delErr } = await supabase
        .from('quote_proof')
        .delete()
        .eq('quote_id', quoteId)
        .eq('proof_id', proofRow.id)

      if (delErr) throw delErr

      await refreshQuotes()
    } catch (err) {
      console.error('Remove proof error:', err)
      toastUpdateFailed(err instanceof Error ? err.message : undefined)
    }
  }

  const showProofToolbar =
    !isShareOrder &&
    !!quoteId &&
    (isAdmin || reuseProofRows.length > 0)

  return (
    <Column className={embedded ? 'gap-3' : 'gap-3 pt-2'}>
      {hasProofs ? (
        <>
          <ProofList
            proofs={proofs}
            layout="review"
            readOnly={false}
            isShareOrder={isShareOrder}
            quoteId={quoteId}
            onRemoveClick={isDemo ? undefined : setProofToRemove}
            onStatusChange={patchProofStatus}
            onCommentChange={isDemo ? () => undefined : patchProofComment}
            embedded={embedded}
            toolbar={
              showProofToolbar ? (
                <ProofUploadButton
                  inputRef={inputRef}
                  disabled={isUploading}
                  canUpload={isAdmin}
                  onFilesSelected={(files) => void handleUpload(files)}
                  reuseProofRows={reuseProofRows}
                  onReuseProofsConfirm={attachExistingProofs}
                />
              ) : undefined
            }
          />

          <DeleteProofDialog
            open={proofToRemove !== null}
            proofName={proofToRemove ? getFilenameFromUrl(proofToRemove) : ''}
            onOpenChange={(open) => !open && setProofToRemove(null)}
            onConfirm={() => {
              if (proofToRemove) {
                handleRemove(proofToRemove)
                setProofToRemove(null)
              }
            }}
          />
        </>
      ) : (
        <Column className={embedded ? 'gap-4' : 'gap-4 px-4'}>
          {showProofToolbar ? (
            <Row className="w-full justify-end">
              <ProofUploadButton
                inputRef={inputRef}
                disabled={isUploading}
                canUpload={isAdmin}
                onFilesSelected={(files) => void handleUpload(files)}
                reuseProofRows={reuseProofRows}
                onReuseProofsConfirm={attachExistingProofs}
              />
            </Row>
          ) : null}
          <VerifyProofsEmptyState
            isShareOrder={isShareOrder}
            withCard={!embedded}
          />
        </Column>
      )}
    </Column>
  )
}
